import React, { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Zoom from '@mui/material/Zoom';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { PropSensorItemCreate, SensorLatest, VisibleSubSensor, GeoJSON } from '../../services/api';
import { PropSensorItemCreateIndexed } from '../../utils/sensorProperties';
import { getAllLocationFloorplan, getSensorsById } from '../../state/selectors';
import { addNewSensor, fetchLocationFloorplan, fetchSensorLatest } from '../../services/apiService';
import PositioningMap from '../Map/PositioningMap';
import { SensorPositionData } from '../Map/types';
import Loading from '../../components/Loading';
import { setLocationFloorplan, setSensorsById } from '../../state/actions';
import { themeProps } from '../../styles/theme';
import InstallSuccessIcon from '../../styles/icons/InstallSuccessIcon';
import { appSwitchBtnHandler } from '../../Shell/helpers';
import { InstallSteps, steps } from './InstallContainer';
import { useAppDispatch, useAppSelector } from '../../state/store';

interface InstallProcessProps {
  gatewayDetails: SensorLatest;
  selectedSubSensor: VisibleSubSensor;
  handleRefresh: () => void;
  activeStep: number;
  setActiveStep: (value: number) => void;
  position: SensorPositionData | undefined;
  setPosition: (value: SensorPositionData | undefined) => void;
}

function InstallProcess({
  gatewayDetails,
  selectedSubSensor,
  handleRefresh,
  activeStep,
  setActiveStep,
  position,
  setPosition,
}: InstallProcessProps): JSX.Element {
  const dispatch = useAppDispatch();
  const allFloorPlan = useAppSelector(getAllLocationFloorplan);
  const sensorsById = useAppSelector(getSensorsById);

  const [floorPlan, setFloorPlan] = useState<GeoJSON>();
  const [sensorPayload, setSensorPayload] = useState<PropSensorItemCreateIndexed>();
  const [creatingSensor, setCreatingSensor] = useState(false);
  const [isSensorCreated, setIsSensorCreated] = useState(false);
  const [fetchingInitialData, setFetchingInitialData] = useState<boolean>(true);

  const getFloorPlan = useCallback(() => {
    if (gatewayDetails?.location) {
      const currentLocationFloorPlan = allFloorPlan.get(gatewayDetails.location);
      const hasFloorPlan =
        currentLocationFloorPlan?.features && currentLocationFloorPlan?.features.length > 0;
      if (hasFloorPlan) {
        setFloorPlan(currentLocationFloorPlan);
      } else {
        fetchLocationFloorplan(gatewayDetails.location)
          .then((floorplan) => {
            setFloorPlan(floorplan);
            dispatch(
              setLocationFloorplan({ locationId: gatewayDetails.location as string, floorplan })
            );
          })
          .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
      }
    }
  }, [allFloorPlan, dispatch, gatewayDetails.location]);

  useEffect(() => {
    getFloorPlan();
    const item: PropSensorItemCreate = {
      location: gatewayDetails?.location ?? '',
      name: '',
      shortName: '',
      position: gatewayDetails?.position,
      gateway: gatewayDetails.id,
      timeZone: gatewayDetails?.timeZone,
      type: selectedSubSensor.type,
      fwVersion: '1',
      hwVersion: selectedSubSensor?.hwVersion ?? '',
    };
    setSensorPayload(item);
  }, [getFloorPlan, gatewayDetails, selectedSubSensor]);

  const handleInitialData = () => {
    setFetchingInitialData(true);
    const interval = setInterval(async () => {
      const sensorLatest = await fetchSensorLatest(selectedSubSensor.suggested_id);
      if (sensorLatest?.data && sensorLatest.data.length > 0) {
        clearInterval(interval);
        setFetchingInitialData(false);
        dispatch(setSensorsById([sensorLatest]));
      }
    }, 1000);
  };

  const handleSubmit = () => {
    setCreatingSensor(true);
    setIsSensorCreated(false);
    const item = { ...sensorPayload };
    item.position = position;
    addNewSensor(selectedSubSensor.suggested_id, item as PropSensorItemCreate)
      .then((res) => {
        setCreatingSensor(false);
        handleInitialData();
        // update sensor Details and pass name to app
        dispatch(setSensorsById([res as SensorLatest]));
        setIsSensorCreated(true);
      })
      .catch((err) => {
        setCreatingSensor(false);
        enqueueSnackbar(err.cause, { variant: 'error', persist: true });
      });
  };

  const textFieldChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    param: string
  ) => {
    const { value } = e.target;
    let item;
    if (param === 'name') {
      const trimmedShortName = value.replace(/ +/g, '').trim().substring(0, 10);
      const capitalizedShortName =
        trimmedShortName.charAt(0).toUpperCase() + trimmedShortName.slice(1);
      item = { ...sensorPayload, name: value, shortName: capitalizedShortName };
    }
    closeSnackbar();
    setSensorPayload(item as PropSensorItemCreate);
  };

  const nextBtnHandler = () => {
    closeSnackbar();
    if (steps[activeStep] === InstallSteps.Position) {
      if (!position)
        enqueueSnackbar('Sensor must be placed in the floorplan', {
          variant: 'error',
          persist: true,
        });
      else setActiveStep(activeStep + 1);
    } else if (steps[activeStep] === InstallSteps.Name) {
      if (!sensorPayload?.name)
        enqueueSnackbar('Name must be given to sensor', { variant: 'error', persist: true });
      else {
        handleSubmit();
        setActiveStep(activeStep + 1);
      }
    }
  };

  const prevBtnHandler = () => {
    closeSnackbar();
    setActiveStep(activeStep - 1);
  };

  const restartProcess = () => {
    setActiveStep(0);
    setPosition(undefined);
    handleRefresh();
    appSwitchBtnHandler(dispatch);
  };

  return (
    <>
      {steps[activeStep] === InstallSteps.Position && (
        <Alert severity="info">Tap to place sensor in the floorplan</Alert>
      )}
      {steps[activeStep] === InstallSteps.Position && (
        <Zoom in={steps[activeStep] === InstallSteps.Position}>
          <Box>
            <PositioningMap
              floorPlan={floorPlan}
              onChange={(pos) => {
                setPosition(pos);
                closeSnackbar();
              }}
              // initial position can be gateway position to zoom in to the place
              position={position}
              mapName="SensorCreate"
              gatewayPosition={gatewayDetails.position}
            />
          </Box>
        </Zoom>
      )}
      {steps[activeStep] === InstallSteps.Name && (
        <Zoom in={steps[activeStep] === InstallSteps.Name}>
          <div style={{ textAlign: 'center' }}>
            <TextField
              type="text"
              required
              label="Name"
              onChange={(e) => textFieldChange(e, 'name')}
              value={sensorPayload?.name ?? ''}
              variant="standard"
              style={{ width: '80%', margin: '10px', padding: '20px' }}
            />
            <TextField
              type="text"
              required
              label="Gateway Name"
              disabled
              onChange={(e) => textFieldChange(e, 'name')}
              value={
                sensorPayload?.gateway
                  ? sensorsById.get(sensorPayload?.gateway)?.name ?? sensorPayload.gateway
                  : 'UNKNOWN'
              }
              variant="standard"
              style={{ width: '80%', margin: '10px', padding: '20px' }}
            />
          </div>
        </Zoom>
      )}
      {steps[activeStep] === InstallSteps.Confirm ? (
        <Zoom in={steps[activeStep] === InstallSteps.Confirm}>
          <div>
            {creatingSensor && <Loading />}
            <Box sx={{ textAlign: 'center' }}>
              {isSensorCreated && (
                <>
                  <InstallSuccessIcon
                    sx={{ width: '100%', fontSize: '100px', marginTop: '5rem' }}
                  />
                  <Typography
                    style={{
                      margin: '20px',
                      display: 'flex',
                      justifyContent: 'left',
                    }}
                  >
                    <TaskAltIcon
                      sx={{ fontSize: '24px', marginRight: '10px', color: themeProps.colors.green }}
                    />
                    Sensor has been created successfully
                  </Typography>
                  {fetchingInitialData ? (
                    <>
                      <Loading />
                      <Typography style={{ marginTop: '20px' }}>Fetching initial data</Typography>
                    </>
                  ) : (
                    <Typography style={{ margin: '20px', display: 'flex', justifyContent: 'left' }}>
                      <TaskAltIcon
                        sx={{
                          fontSize: '24px',
                          marginRight: '10px',
                          color: themeProps.colors.green,
                        }}
                      />
                      Initial Data received
                    </Typography>
                  )}
                </>
              )}
              <Button
                variant="contained"
                style={{ margin: '20px', textTransform: 'capitalize', width: '80%' }}
                onClick={restartProcess}
              >
                Go to New Sensors List
              </Button>
            </Box>
          </div>
        </Zoom>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={prevBtnHandler}
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            style={{ margin: '0 10px' }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={nextBtnHandler}
            endIcon={<KeyboardDoubleArrowRightIcon />}
            style={{ margin: '0 10px' }}
          >
            {steps[activeStep] === InstallSteps.Name ? 'Create Sensor' : 'Next'}
          </Button>
        </Box>
      )}
    </>
  );
}

export default InstallProcess;
